import type { ProjectDocument } from '@timelog/pm-shared';

import { LOG_TIME_SCOPES } from '../sampleData/reach';
import type { LogTimeScope } from '../sampleData/reach';
import type { SettingsSample } from '../sampleData/settingsSample';

import { planFrame } from './planFrame';

/**
 * What Project details holds for one project (docs/briefs/project-details-v2),
 * card by card. Prototype only: kept in this browser, never sent to the
 * backend.
 *
 * Only what the person CHANGED is stored (a patch); the values shown are that
 * patch over defaults read from v1's sample and the project's plan, so "Reset
 * sample data" is simply forgetting the patch.
 */
export type DetailsFile = {
  id: string;
  name: string;
  sizeBytes: number;
};

export type DetailsLink = {
  id: string;
  name: string;
  url: string;
};

export type DetailsValues = {
  // What it's about
  /** HTML from the description editor (ADR 0052); plain text from before reads as lines. */
  description: string;
  files: DetailsFile[];
  links: DetailsLink[];
  // Who's involved — employee ids from the sample; the contact is the customer's.
  projectManagerId: string;
  accountManagerId: string;
  partnerId: string;
  contactId: string | null;
  // Who can log time
  logTimeScope: LogTimeScope;
  askForEtc: boolean;
  // When — ISO days, '' when not set
  startDate: string;
  endDate: string;
  deliveryDate: string;
  // Where it belongs
  legalEntityId: string;
  departmentId: string;
  projectType: string | null;
  projectCategory: string | null;
  includeInResourcePlanning: boolean;
  /** 0–100, whole numbers. */
  likelihoodPct: number;
};

export type DetailsPatch = Partial<DetailsValues>;

/** Starts on everything empty that the brief says starts empty (decision 9). */
export const defaultDetailsValues = (
  sample: SettingsSample,
  document?: Pick<ProjectDocument, 'plan'>,
): DetailsValues => {
  const owners = sample.defaultOwners;
  const frame = planFrame(document);
  return {
    description: '',
    files: [],
    links: [],
    projectManagerId: owners.projectManagerId,
    accountManagerId: owners.accountManagerId,
    partnerId: owners.partnerId,
    contactId: null,
    logTimeScope: 'allocatedOnly',
    askForEtc: false,
    startDate: frame.startDate,
    endDate: frame.endDate,
    deliveryDate: '',
    legalEntityId: owners.legalEntityId,
    departmentId: owners.departmentId,
    projectType: null,
    projectCategory: null,
    includeInResourcePlanning: true,
    likelihoodPct: 100,
  };
};

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseFiles = (raw: unknown): DetailsFile[] | undefined =>
  Array.isArray(raw)
    ? raw.filter(
        (file): file is DetailsFile =>
          isRecord(file) &&
          typeof file.id === 'string' &&
          typeof file.name === 'string' &&
          typeof file.sizeBytes === 'number',
      )
    : undefined;

const parseLinks = (raw: unknown): DetailsLink[] | undefined =>
  Array.isArray(raw)
    ? raw.filter(
        (link): link is DetailsLink =>
          isRecord(link) &&
          typeof link.id === 'string' &&
          typeof link.name === 'string' &&
          typeof link.url === 'string',
      )
    : undefined;

/** A stored patch, field by field; anything unreadable is dropped, never thrown. */
export const parseDetailsPatch = (raw: unknown, sample: SettingsSample): DetailsPatch => {
  if (!isRecord(raw)) return {};
  const patch: DetailsPatch = {};
  const isEmployee = (value: unknown): value is string =>
    typeof value === 'string' && sample.employees.some((employee) => employee.id === value);
  const isContact = (value: unknown): value is string =>
    typeof value === 'string' &&
    Object.values(sample.contacts).some((list) => list.some((contact) => contact.id === value));

  if (typeof raw.description === 'string') patch.description = raw.description;
  const files = parseFiles(raw.files);
  if (files) patch.files = files;
  const links = parseLinks(raw.links);
  if (links) patch.links = links;

  if (isEmployee(raw.projectManagerId)) patch.projectManagerId = raw.projectManagerId;
  if (isEmployee(raw.accountManagerId)) patch.accountManagerId = raw.accountManagerId;
  if (isEmployee(raw.partnerId)) patch.partnerId = raw.partnerId;
  if (raw.contactId === null || isContact(raw.contactId)) patch.contactId = raw.contactId;

  if ((LOG_TIME_SCOPES as readonly unknown[]).includes(raw.logTimeScope)) {
    patch.logTimeScope = raw.logTimeScope as LogTimeScope;
  }
  if (typeof raw.askForEtc === 'boolean') patch.askForEtc = raw.askForEtc;

  for (const key of ['startDate', 'endDate', 'deliveryDate'] as const) {
    const value = raw[key];
    if (value === '' || (typeof value === 'string' && ISO_DAY.test(value))) patch[key] = value;
  }

  if (
    typeof raw.legalEntityId === 'string' &&
    sample.legalEntities.some((entity) => entity.id === raw.legalEntityId)
  ) {
    patch.legalEntityId = raw.legalEntityId;
  }
  if (
    typeof raw.departmentId === 'string' &&
    sample.departments.some((department) => department.id === raw.departmentId)
  ) {
    patch.departmentId = raw.departmentId;
  }
  if (raw.projectType === null || sample.projectTypes.includes(raw.projectType as string)) {
    patch.projectType = raw.projectType as string | null;
  }
  if (
    raw.projectCategory === null ||
    sample.projectCategories.includes(raw.projectCategory as string)
  ) {
    patch.projectCategory = raw.projectCategory as string | null;
  }
  if (typeof raw.includeInResourcePlanning === 'boolean') {
    patch.includeInResourcePlanning = raw.includeInResourcePlanning;
  }
  const pct = raw.likelihoodPct;
  if (typeof pct === 'number' && Number.isInteger(pct) && pct >= 0 && pct <= 100) {
    patch.likelihoodPct = pct;
  }
  return patch;
};
